import React, { useState, useEffect, useRef } from 'react';
import { db } from '../firebase';
import { collection, query, where, getDocs, doc, updateDoc, deleteDoc } from 'firebase/firestore';
import Receipt from './Receipt';

const BarcodeScanner = ({ order, onClose }) => {
  const [currentProductIndex, setCurrentProductIndex] = useState(0);
  const [currentQuantityProcessed, setCurrentQuantityProcessed] = useState(0);
  const [barcode, setBarcode] = useState('');
  const [scannedBarcodes, setScannedBarcodes] = useState([]);
  const [error, setError] = useState('');
  const [showReceipt, setShowReceipt] = useState(false);
  const [allProductsProcessed, setAllProductsProcessed] = useState(false);
  const [isProcessing, setIsProcessing] = useState(false);
  const inputRef = useRef(null);

  const currentProduct = order.cartItems?.[currentProductIndex];
  const totalProducts = order.cartItems?.length || 0;

  useEffect(() => {
    if (inputRef.current && !showReceipt) {
      inputRef.current.focus();
    }
  }, [currentProductIndex, currentQuantityProcessed, showReceipt]);

  const deleteStockBarcodes = async (barcodes) => {
    const errors = [];
    const deletedProducts = [];

    for (const barcode of barcodes) {
      try {
        const stockBasketRef = collection(db, 'stock_basket');
        const basketSnapshot = await getDocs(stockBasketRef);

        let productFound = false;

        for (const basketDoc of basketSnapshot.docs) {
          const basketData = basketDoc.data();
          const stockProductRef = collection(db, 'stock_basket', basketDoc.id, 'stock_product');

          const productQuery = query(stockProductRef, where('barcode', '==', barcode));
          const productSnapshot = await getDocs(productQuery);

          if (!productSnapshot.empty) {
            const productDoc = productSnapshot.docs[0];
            const productData = productDoc.data();

            console.log('Deleting product:', {
              barcode: productData.barcode,
              product_name: productData.product_name,
              product_code: productData.product_code,
              product_id: productData.product_id,
              basket_number: basketData.basket_number,
              basket_type: basketData.basket_type,
              image: productData.image
            });

            await deleteDoc(doc(db, 'stock_basket', basketDoc.id, 'stock_product', productDoc.id));

            deletedProducts.push({
              barcode: productData.barcode,
              product_name: productData.product_name,
              product_code: productData.product_code,
              product_id: productData.product_id,
              basket_number: basketData.basket_number,
              basket_type: basketData.basket_type,
              deleted_at: new Date()
            });

            console.log(`✅ Successfully deleted barcode: ${barcode} from basket: ${basketData.basket_number}`);
            productFound = true;
            break;
          }
        }

        if (!productFound) {
          errors.push(`Product not found for barcode: ${barcode} in any basket`);
        }

      } catch (err) {
        errors.push(`Error processing barcode ${barcode}: ${err.message}`);
        console.error(`❌ Error deleting barcode ${barcode}:`, err);
      }
    }

    if (errors.length > 0) {
      throw new Error(`Failed to delete ${errors.length} barcode(s):\n${errors.join('\n')}`);
    }

    return deletedProducts;
  };

  const updateOrder = async (finalBarcodes) => {
    try {
      const deletedProducts = await deleteStockBarcodes(finalBarcodes);

      const orderRef = doc(db, 'orders', order.id);

      await updateDoc(orderRef, {
        scannedBarcodes: finalBarcodes,
        deletedStockProducts: deletedProducts,
        packagingComplete: true,
        packagingCompletedAt: new Date(),
        ready_for_pickup: true
      });

      console.log('✅ Order updated successfully with', deletedProducts.length, 'products deleted from stock');
    } catch (err) {
      console.error('❌ Error updating order:', err);
      setError('Failed to complete packaging: ' + err.message);
      setAllProductsProcessed(false);
      setShowReceipt(false);
      throw err;
    }
  };

  const handleBarcodeSubmit = async (e) => {
    e.preventDefault();
    setError('');

    if (isProcessing) return;

    if (!barcode || barcode.length < 4) {
      setError('Please enter a valid barcode (minimum 4 digits)');
      return;
    }

    setIsProcessing(true);

    try {
      const productCode = barcode.substring(0, 4);

      const productsRef = collection(db, 'products');
      const q = query(productsRef, where('product_code', '==', productCode));
      const querySnapshot = await getDocs(q);

      if (querySnapshot.empty) {
        setError(`No product found with code: ${productCode}`);
        setBarcode('');
        return;
      }

      const productDoc = querySnapshot.docs[0];
      const scannedProductId = productDoc.id;

      if (scannedProductId !== currentProduct.productId) {
        setError(`Wrong product! Expected: ${currentProduct.name}`);
        setBarcode('');
        return;
      }

      const newScannedBarcodes = [...scannedBarcodes, barcode];
      setScannedBarcodes(newScannedBarcodes);

      const newQuantityProcessed = currentQuantityProcessed + 1;
      setCurrentQuantityProcessed(newQuantityProcessed);
      setBarcode('');

      if (newQuantityProcessed >= currentProduct.quantity) {
        const nextProductIndex = currentProductIndex + 1;

        if (nextProductIndex >= totalProducts) {
          await updateOrder(newScannedBarcodes);
          setAllProductsProcessed(true);
          setShowReceipt(true);
        } else {
          setCurrentProductIndex(nextProductIndex);
          setCurrentQuantityProcessed(0);
        }
      }

    } catch (err) {
      console.error('Error processing barcode:', err);
      setError('Error processing barcode: ' + err.message);
      setBarcode('');
    } finally {
      setIsProcessing(false);
    }
  };

  const handlePrintComplete = () => {
    onClose();
  };

  if (showReceipt) {
    return <Receipt order={order} onClose={handlePrintComplete} />;
  }

  return (
    <div className="min-h-screen bg-gray-100 p-4">
      <div className="max-w-4xl mx-auto">
        {/* Header */}
        <div className="bg-white rounded-lg shadow-md p-6 mb-4">
          <div className="flex justify-between items-center mb-4">
            <h2 className="text-2xl font-bold text-gray-800">Barcode Scanner</h2>
            <button
              onClick={onClose}
              className="bg-red-500 hover:bg-red-600 text-white font-semibold py-2 px-4 rounded-lg transition duration-200"
            >
              Cancel
            </button>
          </div>

          {/* Order Info */}
          <div className="grid grid-cols-2 gap-4 text-sm">
            <div>
              <span className="font-semibold">Customer:</span> {order.customerInfo?.name}
            </div>
            <div>
              <span className="font-semibold">Order ID:</span> {order.orderId}
            </div>
            <div>
              <span className="font-semibold">Phone:</span> {order.customerInfo?.phone}
            </div>
            <div>
              <span className="font-semibold">Consignment:</span> {order.steadfastConsignmentId}
            </div>
          </div>
        </div>

        {/* Progress */}
        <div className="bg-white rounded-lg shadow-md p-6 mb-4">
          <div className="mb-4">
            <div className="flex justify-between text-sm text-gray-600 mb-2">
              <span>Product {currentProductIndex + 1} of {totalProducts}</span>
              <span>{Math.round(((currentProductIndex + (currentQuantityProcessed / currentProduct?.quantity || 0)) / totalProducts) * 100)}% Complete</span>
            </div>
            <div className="w-full bg-gray-200 rounded-full h-3">
              <div
                className="bg-blue-600 h-3 rounded-full transition-all duration-300"
                style={{
                  width: `${((currentProductIndex + (currentQuantityProcessed / currentProduct?.quantity || 0)) / totalProducts) * 100}%`
                }}
              ></div>
            </div>
          </div>
        </div>

        {/* Current Product */}
        {currentProduct && (
          <div className="bg-white rounded-lg shadow-md p-6 mb-4">
            <h3 className="text-xl font-semibold mb-4 text-gray-800">
              Scan Product {currentProductIndex + 1}
            </h3>

            <div className="flex gap-4 mb-6">
              <img
                src={currentProduct.image}
                alt={currentProduct.name}
                className="w-32 h-32 object-cover rounded-lg border-2 border-gray-200"
                onError={(e) => {
                  e.target.src = 'https://via.placeholder.com/128';
                }}
              />
              <div className="flex-1">
                <h4 className="font-semibold text-lg text-gray-800 mb-2">
                  {currentProduct.name}
                </h4>
                <div className="text-sm text-gray-600 space-y-1">
                  <p><span className="font-semibold">Product ID:</span> {currentProduct.productId}</p>
                  <p><span className="font-semibold">Total Quantity:</span> {currentProduct.quantity}</p>
                  {currentProduct.selectedColor && (
                    <p><span className="font-semibold">Color:</span> {typeof currentProduct.selectedColor === 'object' ? currentProduct.selectedColor.name : currentProduct.selectedColor}</p>
                  )}
                  {currentProduct.selectedSize && (
                    <p><span className="font-semibold">Size:</span> {currentProduct.selectedSize}</p>
                  )}
                  <p className="text-lg">
                    <span className="font-semibold text-blue-600">
                      Scanned: {currentQuantityProcessed} / {currentProduct.quantity}
                    </span>
                  </p>
                </div>
              </div>
            </div>

            {/* Barcode Input */}
            <form onSubmit={handleBarcodeSubmit} className="mb-4">
              <label className="block text-gray-700 text-sm font-bold mb-2">
                Scan Barcode (Item {currentQuantityProcessed + 1} of {currentProduct.quantity})
              </label>
              <div className="flex gap-2">
                <input
                  ref={inputRef}
                  type="text"
                  value={barcode}
                  onChange={(e) => setBarcode(e.target.value)}
                  className="flex-1 px-4 py-3 border-2 border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 text-lg font-mono"
                  placeholder="Scan or enter barcode..."
                  autoFocus
                  disabled={isProcessing}
                />
                <button
                  type="submit"
                  disabled={isProcessing}
                  className="bg-blue-600 hover:bg-blue-700 disabled:bg-blue-300 disabled:cursor-not-allowed text-white font-bold py-3 px-8 rounded-lg transition duration-200"
                >
                  {isProcessing ? 'Processing...' : 'Submit'}
                </button>
              </div>
            </form>

            {error && (
              <div className="bg-red-100 border-l-4 border-red-500 text-red-700 p-4 rounded">
                <p className="font-semibold">Error</p>
                <p>{error}</p>
              </div>
            )}

            {/* Scanned Items for Current Product */}
            {currentQuantityProcessed > 0 && (
              <div className="mt-4 p-4 bg-green-50 rounded-lg">
                <p className="text-sm font-semibold text-green-800 mb-2">
                  Scanned Barcodes for this product:
                </p>
                <div className="flex flex-wrap gap-2">
                  {scannedBarcodes.slice(-currentQuantityProcessed).map((code, index) => (
                    <span
                      key={index}
                      className="bg-green-200 text-green-800 px-3 py-1 rounded-full text-sm font-mono"
                    >
                      {code}
                    </span>
                  ))}
                </div>
              </div>
            )}
          </div>
        )}

        {/* All Scanned Products */}
        {currentProductIndex > 0 && (
          <div className="bg-white rounded-lg shadow-md p-6">
            <h3 className="text-lg font-semibold mb-4 text-gray-800">
              Completed Products ({currentProductIndex})
            </h3>
            <div className="space-y-2">
              {order.cartItems.slice(0, currentProductIndex).map((product, index) => (
                <div key={index} className="flex items-center gap-3 p-3 bg-green-50 rounded-lg">
                  <img
                    src={product.image}
                    alt={product.name}
                    className="w-12 h-12 object-cover rounded"
                    onError={(e) => {
                      e.target.src = 'https://via.placeholder.com/48';
                    }}
                  />
                  <div className="flex-1">
                    <p className="text-sm font-semibold text-gray-800">{product.name}</p>
                    <p className="text-xs text-gray-600">Qty: {product.quantity} ✓</p>
                    {product.selectedColor && (
                      <p className="text-xs text-gray-500">Color: {typeof product.selectedColor === 'object' ? product.selectedColor.name : product.selectedColor}</p>
                    )}
                    {product.selectedSize && (
                      <p className="text-xs text-gray-500">Size: {product.selectedSize}</p>
                    )}
                  </div>
                  <svg className="w-6 h-6 text-green-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                  </svg>
                </div>
              ))}
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default BarcodeScanner;